/**
 * @fileoverview Session Replay (rrweb) capture.
 * @module features/analytics/replay
 *
 * @description
 * Browser-only DOM session replay powered by [rrweb](https://github.com/rrweb-io/rrweb).
 * Records DOM mutations, mouse, scroll, input, and viewport events, batches them
 * into chunks, and ships them to the FlowGrid backend at `call=replay_chunk`.
 *
 * Loaded dynamically so it is only pulled into the bundle when `start()` is
 * called in a browser environment.
 *
 * @example
 * ```ts
 * const stop = await fg.replay.start({
 *   sampleRate: 1.0,
 *   maskAllInputs: true,
 * });
 * // ...later
 * stop();
 * ```
 */

import { FlowGridTransport } from "../../client/transport";
import { isTrackingSuppressed } from "../../utils/suppression";
import { isBrowser, getDocument, type Teardown } from "../../utils/browser";

// ============================================================================
// CONSENT
// ============================================================================

const REPLAY_CONSENT_KEY = "flowgrid:replayConsent";

/**
 * Returns true if the user has previously granted replay consent
 * (persisted in localStorage across sessions).
 */
export function hasReplayConsent(): boolean {
  if (!isBrowser()) return false;
  try {
    return localStorage.getItem(REPLAY_CONSENT_KEY) === "granted";
  } catch {
    return false;
  }
}

/**
 * Persist replay consent in localStorage.
 * Call this from your cookie/consent banner "Accept" handler.
 * Once granted, the banner will not need to re-prompt on subsequent visits
 * and `SessionReplay.start()` will proceed without being blocked.
 *
 * @example
 * ```ts
 * // In your consent banner "Accept All" click handler:
 * grantReplayConsent();
 * ```
 */
export function grantReplayConsent(): void {
  if (!isBrowser()) return;
  try {
    localStorage.setItem(REPLAY_CONSENT_KEY, "granted");
  } catch { /* storage unavailable */ }
}

/**
 * Revoke replay consent and remove the persisted value.
 * Recording will be blocked on the next `start()` call when
 * `requireConsent: true` is set.
 */
export function revokeReplayConsent(): void {
  if (!isBrowser()) return;
  try {
    localStorage.removeItem(REPLAY_CONSENT_KEY);
  } catch { /* storage unavailable */ }
}

// ============================================================================
// CONFIG
// ============================================================================

export interface SessionReplayOptions {

  /** Explicit enable switch. Pass `false` to keep the recorder disabled. */
  enabled?: boolean;

  /**
   * Gate recording on explicit user consent.
   * When `true`, `start()` will only proceed if `hasReplayConsent()` returns
   * `true` (localStorage) or `FlowGridTransport.hasConsent("analytics")` is set.
   * Call `grantReplayConsent()` from your consent banner to unblock recording.
   * Default `false` (backwards-compatible — does not break existing installs).
   */
  requireConsent?: boolean;

  /** Sampling rate 0.0-1.0. Default 1.0 (record every session). */
  sampleRate?: number;

  /** Existing session ID to attach the recording to. Auto-generated otherwise. */
  sessionId?: string;

  /** User ID for the recording, if known. */
  userId?: string;

  /** Mask all `<input>`, `<textarea>` and `[contenteditable]` values. Default true. */
  maskAllInputs?: boolean;

  /** Mask text content under elements matching this CSS class. Default `"fg-mask"`. */
  maskTextClass?: string | RegExp;

  /** Skip recording elements matching this CSS class entirely. Default `"fg-block"`. */
  blockClass?: string | RegExp;

  /** Ignore events on elements matching this CSS class. Default `"fg-ignore"`. */
  ignoreClass?: string;

  /** Flush buffered events at most every N ms. Default 5000. */
  flushIntervalMs?: number;

  /** Force flush when buffer reaches N bytes (approximate). Default 512KB. */
  flushBytes?: number;

  /** Maximum recording length per session in ms. Default 30 minutes. */
  maxDurationMs?: number;

  /** Hard cap on total bytes captured per session. Default 10MB. */
  maxBytes?: number;

  /** Invoked when recorder state changes or lifecycle metadata is updated. */
  onStatus?: (status: SessionReplayStatus) => void;

  /** Invoked after a chunk is accepted for transport. */
  onFlush?: (metadata: SessionReplayFlushMetadata) => void;

  /** Invoked when events are dropped due to limits or serialization issues. */
  onDrop?: (metadata: SessionReplayDropMetadata) => void;

  /** Invoked when recorder setup or teardown fails. */
  onError?: (error: Error, context: SessionReplayErrorContext) => void;

}

export type SessionReplayState =
  | "idle"
  | "starting"
  | "recording"
  | "stopped"
  | "disabled"
  | "sampled_out"
  | "error";

export type SessionReplayFlushReason =
  | "interval"
  | "size"
  | "visibility_hidden"
  | "pagehide"
  | "manual"
  | "stop"
  | "max_duration"
  | "max_bytes";

export interface SessionReplayStatus {
  state: SessionReplayState;
  sessionId?: string;
  userId?: string;
  startedAt?: string;
  sequence: number;
  bufferedEvents: number;
  bufferedBytes: number;
  totalBytes: number;
  droppedEvents: number;
  recorderVersion: string;
  lastFlushReason?: SessionReplayFlushReason;
  lastError?: string;
}

export interface SessionReplayFlushMetadata {
  sessionId: string;
  userId?: string;
  sequence: number;
  eventsCount: number;
  byteSize: number;
  reason: SessionReplayFlushReason;
  isFinal: boolean;
  droppedEvents: number;
  capturedAt: string;
  idempotencyKey: string;
}

export interface SessionReplayDropMetadata {
  sessionId?: string;
  reason: "serialization_error" | "max_bytes";
  droppedEvents: number;
  byteSize?: number;
  maxBytes?: number;
}

export interface SessionReplayErrorContext {
  phase: "import" | "record" | "flush" | "stop";
  sessionId?: string;
}

export interface SessionReplaySendChunkOptions {
  userId?: string;
  sequence?: number;
  useBeacon?: boolean;
  startedAt?: string;
  capturedAt?: string;
  pageUrl?: string;
  isFinal?: boolean;
  reason?: SessionReplayFlushReason;
  privacy?: ReplayPrivacyMetadata;
  droppedEvents?: number;
  recorderVersion?: string;
}

export interface ReplayPrivacyMetadata {
  masking_mode: "all_inputs" | "selective" | "custom";
  blocked_selectors: string[];
  redacted_fields: string[];
  consent_state: "granted" | "denied" | "unknown";
  sampling_state: "sampled_in" | "sampled_out";
}

interface ActiveRecording {
  sessionId: string;
  userId?: string;
  startedAtMs: number;
  startedAt: string;
  sequence: number;
  buffer: unknown[];
  bufferBytes: number;
  totalBytes: number;
  droppedEvents: number;
  stopped: boolean;
  /** Set to true once the first FullSnapshot (rrweb event type 2) is in the buffer. */
  initialSnapshotCaptured: boolean;
  /** Re-snapshot attempts after a failed snapshot chunk — bounded, see flush(). */
  snapshotRetries: number;
  flushTimer: ReturnType<typeof setInterval>;
  durationGuard: ReturnType<typeof setTimeout>;
  stopRecording?: Teardown;
  /** True while the recorder is torn down because the tab is hidden. */
  paused: boolean;
  /**
   * Pending teardown for a tab that just went hidden. Cancelled if the visitor
   * comes straight back — see HIDDEN_GRACE_MS.
   */
  hideTimer?: ReturnType<typeof setTimeout>;
  /** rrweb is imported lazily inside start(); held here so resume can re-enter it. */
  rrweb: typeof import("rrweb");
  onVisibility: () => void;
  onPageHide: () => void;
  options: Required<Pick<SessionReplayOptions, "flushIntervalMs" | "flushBytes" | "maxDurationMs" | "maxBytes" | "maskAllInputs">> & SessionReplayOptions;
}

/**
 * How long a tab must stay hidden before the recorder is actually torn down.
 *
 * Tearing down on `hidden` is correct (see `pauseForHidden`) but the restart
 * emits a fresh FullSnapshot — a serialised copy of the ENTIRE DOM, measured at
 * ~520 KB on a production page. Doing that on every alt-tab is brutal: one real
 * 85-second session carried THREE snapshots totalling 1.59 MB, while the actual
 * mutations came to 69 KB. 96% of that session's upload was the same page, sent
 * three times, because the visitor switched tabs.
 *
 * Ten seconds covers the overwhelmingly common case — glance at another tab,
 * come straight back — during which a hidden page produces almost no mutations
 * anyway, so the desync risk the teardown guards against barely applies. Longer
 * absences still tear down exactly as before.
 */
const HIDDEN_GRACE_MS = 10_000;

const DEFAULT_FLUSH_MS = 5_000;
const DEFAULT_FLUSH_BYTES = 512 * 1024;
// Interval flushes skip buffers smaller than this — a quiet page (visitor
// reading, idle tab) accumulates instead of dripping a tiny chunk every tick.
// Must stay well under the ~64KB unload keepalive/sendBeacon caps so the
// pagehide/visibility flush can always ship what accumulated.
const MIN_INTERVAL_FLUSH_BYTES = 16 * 1024;
const DEFAULT_MAX_DURATION_MS = 30 * 60 * 1000;
const DEFAULT_MAX_BYTES = 10 * 1024 * 1024;
/** Re-snapshot attempts after a snapshot chunk fails to send. */
const MAX_SNAPSHOT_RETRIES = 2;
const STORAGE_KEY = "flowgrid:replaySessionId";

// Last time this replay session saw activity. Without it the session id was
// pinned to sessionStorage alone, which lives for the lifetime of the browser
// TAB — a tab left open for days kept reusing one id, so every chunk it ever
// flushed aggregated into a single "session". The dashboard computes duration
// as (last chunk − first chunk) grouped by session_id, which is how a visitor
// who left a tab open over a weekend showed up as a 95-hour session replay.
//
// `maxDurationMs` does not prevent this: it stops the *recorder*, not the id,
// so the next run appended to the same session.
const TOUCH_STORAGE_KEY = "flowgrid:replaySessionTouch";

// Idle gap after which the replay session is considered over and a new id is
// minted. Deliberately the same 30 minutes as IdentityManager's
// `sessionTimeoutMinutes` default (utils/identity.ts) — replay sessions and
// analytics sessions describing the same visit must not disagree about where
// one visit ends and the next begins.
const SESSION_IDLE_TIMEOUT_MS = 30 * 60 * 1000;
// Chunk sequence numbers must keep increasing across PAGE LOADS of the same
// session: the session id lives in sessionStorage and survives navigation, but
// each page load creates a fresh ActiveRecording. Restarting at 0 produced
// duplicate sequence numbers within one session, and the dashboard player
// (which orders chunks by sequence) interleaved different page recordings —
// replays looked broken/jumpy. Persist the next sequence per session so every
// page load continues where the previous one stopped.
const SEQ_STORAGE_PREFIX = "flowgrid:replaySeq:";
const RECORDER_VERSION = "rrweb@2";

function loadPersistedSequence(sessionId: string): number {
  try {
    const raw = sessionStorage.getItem(SEQ_STORAGE_PREFIX + sessionId);
    const parsed = raw == null ? NaN : Number(raw);
    return Number.isInteger(parsed) && parsed >= 0 ? parsed : 0;
  } catch {
    return 0;
  }
}

function persistSequence(sessionId: string, nextSequence: number): void {
  try {
    sessionStorage.setItem(SEQ_STORAGE_PREFIX + sessionId, String(nextSequence));
  } catch { /* storage unavailable */ }
}

// ============================================================================
// SESSION REPLAY CLASS
// ============================================================================

export class SessionReplay extends FlowGridTransport {
  private disabled = false;
  private active: ActiveRecording | null = null;
  private state: SessionReplayState = "idle";
  private boundUserId?: string;
  private lastError?: string;

  /**
   * Begin capturing the current page as a replay session. Returns a teardown
   * function that flushes the final buffer (via `sendBeacon` when possible)
   * and detaches the recorder.
   *
   * No-op on the server or when the browser fails sampling.
   */
  async start(options: SessionReplayOptions = {}): Promise<Teardown> {

    if (options.enabled === false || this.disabled) {
      this.setState("disabled", options);
      return () => {};
    }

    // Global kill switch — outranks `requireConsent` and every replay option.
    // Checked before anything else so a suppressed visitor never has rrweb
    // attached to their DOM at all, let alone a chunk uploaded.
    if (isTrackingSuppressed()) {
      this.setState("disabled", options);
      return () => {};
    }

    if (this.active && !this.active.stopped) {
      this.emitStatus(this.active.options);
      return () => this.stop("manual");
    }

    if (!isBrowser()) return () => {};

    // Consent gate: when requireConsent is true, recording only proceeds if the
    // user has explicitly granted consent (localStorage key or transport consent).
    if (options.requireConsent === true) {
      const consented = hasReplayConsent() || FlowGridTransport.hasConsent("analytics");
      if (!consented) {
        this.setState("disabled", options);
        return () => {};
      }
    }

    this.setState("starting", options);

    const sampleRate = options.sampleRate ?? 1.0;
    if (sampleRate < 1 && Math.random() > sampleRate) {
      this.setState("sampled_out", options);
      return () => {};
    }

    let rrweb: typeof import("rrweb");
    try {
      rrweb = await import(/* webpackChunkName: "flowgrid-replay" */ "rrweb");
    } catch (error) {
      // Surface the failure — silent import errors are the #1 reason replays
      // never start without any indication in the dashboard.
      if (typeof console !== "undefined") {
        console.error("[FlowGrid] Session replay failed to load rrweb:", error);
      }
      this.handleError(this.toError(error), { phase: "import" }, options);
      return () => {};
    }

    const doc = getDocument();
    if (!doc) return () => {};

    const sessionId = options.sessionId ?? this.resolveSessionId();
    const normalizedOptions = this.normalizeOptions(options);
    const startedAtMs = Date.now();
    
    const active: ActiveRecording = {
      sessionId,
      userId: options.userId ?? this.boundUserId,
      startedAtMs,
      startedAt: new Date(startedAtMs).toISOString(),
      sequence: loadPersistedSequence(sessionId),
      buffer: [],
      bufferBytes: 0,
      totalBytes: 0,
      droppedEvents: 0,
      stopped: false,
      paused: false,
      rrweb,
      initialSnapshotCaptured: false,
      snapshotRetries: 0,
      flushTimer: setInterval(() => { void this.flush("interval"); }, normalizedOptions.flushIntervalMs),
      durationGuard: setTimeout(() => this.stop("max_duration"), normalizedOptions.maxDurationMs),
      onVisibility: () => {
        const current = this.active;
        if (!current) return;

        if (doc.visibilityState === "hidden") {
          // Flush immediately and via beacon — the tab may never come back, and
          // this is unrelated to whether we go on to tear the recorder down.
          void this.flush("visibility_hidden", { useBeacon: true });

          // Defer the teardown. A visitor who returns inside the grace window
          // never pays for a fresh full-DOM snapshot.
          if (current.hideTimer) clearTimeout(current.hideTimer);
          current.hideTimer = setTimeout(() => {
            current.hideTimer = undefined;
            this.pauseForHidden();
          }, HIDDEN_GRACE_MS);
        } else {
          // Back within the grace window: cancel the pending teardown and do
          // nothing at all. The recorder never stopped, so there is no mirror to
          // repair and no snapshot to re-send.
          if (current.hideTimer) {
            clearTimeout(current.hideTimer);
            current.hideTimer = undefined;
          }
          // No-ops unless we actually paused.
          this.resumeFromHidden();
        }
      },
      onPageHide: () => { void this.flush("pagehide", { useBeacon: true }); },
      options: normalizedOptions,
    };

    this.active = active;

    try {

      active.stopRecording = this.startRecorder(active);

    } catch (error) {

      this.handleError(this.toError(error), { 
        phase: "record", sessionId 
      }, normalizedOptions);

      active.stopped = true;

      this.cleanupActive("stop");

      this.active = null;

      return () => {};

    }

    doc.addEventListener("visibilitychange", active.onVisibility);
    window.addEventListener("pagehide", active.onPageHide);

    this.setState("recording", normalizedOptions);

    return () => this.stop("manual");
  }

  /** Enable replay capture and start recording immediately. */
  enable(options: SessionReplayOptions = {}): Promise<Teardown> {
    this.disabled = false;
    return this.start({ ...options, enabled: true });
  }

  /** Disable replay capture and stop any active recording. */
  disable(): void {
    this.disabled = true;
    this.stop("manual");
    this.setState("disabled", this.active?.options);
  }

  /** Stop recording and flush the final chunk with lifecycle metadata. */
  stop(reason: SessionReplayFlushReason = "stop"): void {
    const active = this.active;
    if (!active || active.stopped) return;
    active.stopped = true;
    this.cleanupActive(reason);
    void this.flush(reason, { useBeacon: true, isFinal: true });
    this.setState("stopped", active.options, reason);
    this.active = null;
  }

  /** Flush buffered rrweb events without stopping the recorder. */
  async flush(
    reason: SessionReplayFlushReason = "manual",
    options: { useBeacon?: boolean; isFinal?: boolean } = {},
  ): Promise<void> {

    const active = this.active;
    if (!active || active.buffer.length === 0) return;

    // Quiet-page gate: let small buffers keep accumulating between interval
    // ticks. Every other reason (pagehide, visibility_hidden, size, stop,
    // manual) still flushes whatever is buffered.
    if (reason === "interval" && active.bufferBytes < MIN_INTERVAL_FLUSH_BYTES) return;

    // A recording that is still shipping chunks is not idle. Without this, a
    // recording running longer than SESSION_IDLE_TIMEOUT_MS would rotate its
    // own id on the next resolve and split one continuous visit into two.
    this.touchSession();

    const events = active.buffer;
    const eventsCount = events.length;
    const byteSize = active.bufferBytes;
    // Does THIS chunk carry the DOM? A session whose snapshot is lost is
    // unplayable no matter how many incremental chunks follow it, so the
    // failure path below treats it differently from any other chunk.
    const carriesSnapshot = events.some((e) => (e as { type?: number })?.type === 2);
    const sequence = active.sequence++;
    persistSequence(active.sessionId, active.sequence);
    active.buffer = [];
    active.bufferBytes = 0;

    const capturedAt = new Date().toISOString();
    const idempotencyKey = this.createIdempotencyKey(active.sessionId, sequence, byteSize);
    const metadata: SessionReplayFlushMetadata = {
      sessionId: active.sessionId,
      userId: active.userId,
      sequence,
      eventsCount,
      byteSize,
      reason,
      isFinal: options.isFinal === true,
      droppedEvents: active.droppedEvents,
      capturedAt,
      idempotencyKey,
    };

    try {
      await this.postReplayChunk(active.sessionId, events, {
        userId: active.userId,
        sequence,
        useBeacon: options.useBeacon,
        startedAt: active.startedAt,
        capturedAt,
        pageUrl: this.getPageUrl(),
        isFinal: metadata.isFinal,
        reason,
        privacy: this.createPrivacyMetadata(active.options, "sampled_in"),
        droppedEvents: active.droppedEvents,
        recorderVersion: RECORDER_VERSION,
      }, idempotencyKey);
      active.options.onFlush?.(metadata);
      this.emitStatus(active.options, reason);
    } catch (error) {
      this.handleError(this.toError(error), { phase: "flush", sessionId: active.sessionId }, active.options);
      // The buffer was cleared and the sequence burned BEFORE the send, so a
      // failed chunk is gone. For an ordinary chunk that costs a few seconds of
      // replay. For the chunk holding the FullSnapshot it costs the entire
      // session: rrweb has no DOM to build on, so the player renders a blank
      // stage forever while the incremental chunks that follow keep arriving
      // and keep the session looking healthy in the list.
      //
      // That is exactly what happened on demo.flow-grid.xyz (2026-09-23): the
      // ingest route capped JSON bodies at 1 MB, that site's snapshot is
      // larger, and every one of its replays died at sequence 0 while the
      // smaller marketing pages recorded fine. The server limit is the fix;
      // this is the recovery, so a rejected snapshot can never silently cost
      // the whole recording again.
      // BOUNDED. A snapshot that fails because it is too large will fail
      // again at the same size, and an unbounded retry would spin: re-snapshot
      // → flush → reject → re-snapshot, serialising the whole DOM every time.
      // Two attempts covers a transient network blip and gives up on a
      // permanent rejection.
      if (carriesSnapshot && active.snapshotRetries < MAX_SNAPSHOT_RETRIES) {
        active.snapshotRetries++;
        active.initialSnapshotCaptured = false;
        try {
          active.rrweb.record.takeFullSnapshot?.();
        } catch {
          /* recorder already torn down — nothing to re-snapshot */
        }
      }
    }
  }

  /** Bind a known user to subsequent chunks in the active recording. */
  identify(userId: string): void {
    this.boundUserId = userId;
    if (this.active) {
      this.active.userId = userId;
      this.emitStatus(this.active.options);
    }
  }

  /** Current recorder health/lifecycle state. */
  getStatus(): SessionReplayStatus {
    return this.createStatus(this.active?.options);
  }

  /**
   * Manually persist a single rrweb event chunk. Most callers should use
   * `start()` instead; this is exposed for tests and advanced integrations
   * that drive `rrweb.record()` themselves.
   */
  async sendChunk(
    sessionId: string,
    events: unknown[],
    options: SessionReplaySendChunkOptions = {},
  ): Promise<void> {
    if (events.length === 0) return;
    await this.postReplayChunk(sessionId, events, options);
  }

  private async postReplayChunk(
    sessionId: string,
    events: unknown[],
    options: SessionReplaySendChunkOptions,
    idempotencyKey = this.createIdempotencyKey(sessionId, options.sequence ?? 0, this.measureEvents(events)),
  ): Promise<void> {
    await this.postRaw(
      "replay_chunk",
      {
        type: "replay_chunk",
        session_id: sessionId,
        user_id: options.userId,
        sequence: options.sequence ?? 0,
        started_at: options.startedAt,
        captured_at: options.capturedAt ?? new Date().toISOString(),
        events,
        page_url: options.pageUrl ?? this.getPageUrl(),
        idempotency_key: idempotencyKey,
        compression: "none",
        is_final: options.isFinal === true,
        chunk_kind: options.isFinal ? "final" : "partial",
        flush_reason: options.reason ?? "manual",
        dropped_events: options.droppedEvents ?? 0,
        recorder_version: options.recorderVersion ?? RECORDER_VERSION,
        privacy: options.privacy ?? this.createPrivacyMetadata(undefined, "sampled_in"),
      },
      { useBeacon: options.useBeacon },
    );
  }

  /**
   * Starts rrweb against the current DOM and returns its teardown.
   *
   * Extracted so `resume()` re-enters with the IDENTICAL configuration — a
   * resume that differed from the initial start (different mask classes, a
   * different checkout interval) would produce a recording that changes
   * behaviour halfway through, which is very hard to debug from a replay.
   */
  /**
   * Tears the recorder down while the tab is hidden.
   *
   * Two reasons, and the first is a correctness bug rather than an optimisation:
   *
   * 1. **rrweb desyncs when backgrounded.** Browsers throttle rAF and timers on
   *    a hidden tab, so mutations batch and arrive out of order. The player then
   *    applies "add node" against a stale mirror and inserts DUPLICATES — the
   *    stacked-elements artefact, and the "Node with id N not found" warnings
   *    the dashboard player suppresses. Not recording while hidden cannot
   *    desync.
   * 2. **Nobody is there.** A tab left open overnight recorded hours of dead
   *    air: storage, bandwidth and quota spent on a visitor who left, plus
   *    session durations like "95h 31m" that make the whole feature look broken.
   *
   * The buffer is flushed by the caller BEFORE this runs, so nothing already
   * captured is lost — the tab may never come back.
   */
  private pauseForHidden(): void {
    const active = this.active;
    if (!active || active.stopped || active.paused) return;
    active.paused = true;
    try {
      active.stopRecording?.();
    } catch {
      // A recorder that fails to tear down must not break the page.
    }
    active.stopRecording = undefined;
  }

  /**
   * Restarts the recorder when the visitor comes back to the tab.
   *
   * The restart is what repairs the mirror: `rrweb.record` emits a fresh
   * FullSnapshot of the CURRENT DOM, so the player rebuilds from a known-good
   * tree instead of trying to reconcile mutations it missed while paused.
   * Resuming the old recorder instead would carry the stale mirror across the
   * gap, which is the bug this is here to prevent.
   */
  private resumeFromHidden(): void {
    const active = this.active;
    if (!active || active.stopped || !active.paused) return;
    active.paused = false;
    try {
      active.stopRecording = this.startRecorder(active);
    } catch (error) {
      // Never throw into a visibility handler — a failed resume degrades to
      // "no more recording for this page", never a broken host page.
      this.handleError(this.toError(error), { phase: "record", sessionId: active.sessionId }, active.options);
      active.stopped = true;
    }
  }

  private startRecorder(active: ActiveRecording): Teardown {
    // rrweb returns undefined when it declines to record (e.g. no document).
    // Normalise to a no-op teardown so callers never branch on it.
    return active.rrweb.record({
      emit: (event: unknown) => this.captureEvent(event),
      maskAllInputs: active.options.maskAllInputs,
      // Underscore form is canonical (`fg_mask` / `fg_block` / `fg_ignore`),
      // matching the `fg_ignore` suppression cookie. The hyphenated form is
      // still honoured for mask/block via regex — those were documented, and
      // silently dropping them would un-mask a customer's sensitive elements
      // the moment they upgraded, which is the worst possible failure mode for
      // a privacy control.
      //
      // `ignoreClass` is the exception: rrweb types it as `string`, not
      // `string | RegExp`, so it can hold exactly ONE name. It keeps the
      // hyphenated `fg-ignore` — the form already shipped and documented.
      // Switching it to the underscore would silently un-ignore every element a
      // customer had marked, and they would find out by discovering recorded
      // interactions they had explicitly excluded. A privacy control may not
      // fail open on upgrade; cosmetic consistency with the cookie name is not
      // worth that, so this one stays hyphenated until rrweb accepts a RegExp.
      maskTextClass: active.options.maskTextClass ?? /(?:^|\s)fg[-_]mask(?:\s|$)/,
      blockClass: active.options.blockClass ?? /(?:^|\s)fg[-_]block(?:\s|$)/,
      ignoreClass: active.options.ignoreClass ?? "fg-ignore",
      // Emit a periodic FullSnapshot so the player can seek into the middle
      // of a session without replaying from the start. Every 2 minutes — NOT
      // more often: a FullSnapshot serializes the entire DOM (measured
      // ~850 KB on production sites), so a 30 s checkout multiplied payloads
      // ~10x, hit the 10 MB per-page maxBytes cap within minutes (killing the
      // recording), and made sessions tens of MB to play back — enough to
      // crash iOS Safari during playback. The event-count checkout is
      // deliberately omitted: on busy pages it fired far more often than the
      // time gate and provides no seek benefit the time gate doesn't.
      // Keep in sync with the dashboard player's seek window
      // (vission_manager ReplayPlayer seekWindowMs), which must span at
      // least one checkout interval.
      checkoutEveryNms: 120_000,
      // Reduce snapshot payload size: don't inline images or collect fonts
      // in the snapshot — they can bloat the first chunk significantly and
      // increase the chance of the Flowgrid 1 MB limit being hit on chunk 0.
      inlineImages: false,
      collectFonts: false,
    }) ?? (() => {});
  }

  private captureEvent(event: unknown): void {
    const active = this.active;
    if (!active || active.stopped) return;

    let size = 0;
    try {
      size = JSON.stringify(event).length;
    } catch {
      active.droppedEvents++;
      active.options.onDrop?.({
        sessionId: active.sessionId,
        reason: "serialization_error",
        droppedEvents: active.droppedEvents,
      });
      return;
    }

    if (active.totalBytes + size > active.options.maxBytes) {
      active.droppedEvents++;
      active.options.onDrop?.({
        sessionId: active.sessionId,
        reason: "max_bytes",
        droppedEvents: active.droppedEvents,
        byteSize: active.totalBytes + size,
        maxBytes: active.options.maxBytes,
      });
      this.stop("max_bytes");
      return;
    }

    active.totalBytes += size;
    active.buffer.push(event);
    active.bufferBytes += size;

    // FullSnapshot (rrweb type 2) is always the first event emitted by rrweb.record().
    // Flush it immediately in the next microtask rather than waiting for the
    // 5-second interval — this guarantees sequence 0 is transmitted right away
    // and reduces the window in which a Tinybird network failure can lose the
    // only snapshot the player needs to initialize.
    if (!active.initialSnapshotCaptured && (event as { type?: number }).type === 2) {
      active.initialSnapshotCaptured = true;
      Promise.resolve().then(() => { void this.flush("manual"); });
      return;
    }

    if (active.bufferBytes >= active.options.flushBytes) {
      void this.flush("size");
    }
  }

  private cleanupActive(reason: SessionReplayFlushReason): void {
    const active = this.active;
    if (!active) return;
    const doc = getDocument();
    clearInterval(active.flushTimer);
    clearTimeout(active.durationGuard);
    // A deferred hidden-teardown must not outlive the recording it belongs to.
    if (active.hideTimer) {
      clearTimeout(active.hideTimer);
      active.hideTimer = undefined;
    }
    doc?.removeEventListener("visibilitychange", active.onVisibility);
    window.removeEventListener("pagehide", active.onPageHide);
    try {
      active.stopRecording?.();
    } catch (error) {
      this.handleError(this.toError(error), { phase: "stop", sessionId: active.sessionId }, active.options);
    }
    this.emitStatus(active.options, reason);
  }

  private normalizeOptions(options: SessionReplayOptions): ActiveRecording["options"] {
    return {
      ...options,
      flushIntervalMs: options.flushIntervalMs ?? DEFAULT_FLUSH_MS,
      flushBytes: options.flushBytes ?? DEFAULT_FLUSH_BYTES,
      maxDurationMs: options.maxDurationMs ?? DEFAULT_MAX_DURATION_MS,
      maxBytes: options.maxBytes ?? DEFAULT_MAX_BYTES,
      maskAllInputs: options.maskAllInputs ?? true,
    };
  }

  private createStatus(options?: SessionReplayOptions, reason?: SessionReplayFlushReason): SessionReplayStatus {
    const active = this.active;
    return {
      state: this.state,
      sessionId: active?.sessionId,
      userId: active?.userId ?? this.boundUserId,
      startedAt: active?.startedAt,
      sequence: active?.sequence ?? 0,
      bufferedEvents: active?.buffer.length ?? 0,
      bufferedBytes: active?.bufferBytes ?? 0,
      totalBytes: active?.totalBytes ?? 0,
      droppedEvents: active?.droppedEvents ?? 0,
      recorderVersion: RECORDER_VERSION,
      lastFlushReason: reason,
      lastError: this.lastError,
    };
  }

  private setState(state: SessionReplayState, options?: SessionReplayOptions, reason?: SessionReplayFlushReason): void {
    this.state = state;
    this.emitStatus(options, reason);
  }

  private emitStatus(options?: SessionReplayOptions, reason?: SessionReplayFlushReason): void {
    options?.onStatus?.(this.createStatus(options, reason));
  }

  private handleError(error: Error, context: SessionReplayErrorContext, options?: SessionReplayOptions): void {
    this.lastError = error.message;
    this.state = "error";
    options?.onError?.(error, context);
    this.emitStatus(options);
  }

  private createPrivacyMetadata(
    options?: SessionReplayOptions,
    samplingState: ReplayPrivacyMetadata["sampling_state"] = "sampled_in",
  ): ReplayPrivacyMetadata {
    // Check both the persistent localStorage consent and the in-memory transport
    // consent gate so either path to granting consent is reflected accurately.
    const consentGranted = hasReplayConsent() || FlowGridTransport.hasConsent("analytics");
    return {
      masking_mode: options?.maskAllInputs === false ? "selective" : "all_inputs",
      blocked_selectors: this.selectorList(options?.blockClass ?? "fg-block"),
      redacted_fields: this.selectorList(options?.maskTextClass ?? "fg-mask"),
      consent_state: consentGranted ? "granted" : "denied",
      sampling_state: samplingState,
    };
  }

  private selectorList(selector: string | RegExp): string[] {
    return typeof selector === "string" ? [selector] : [selector.source];
  }

  private getPageUrl(): string {
    try {
      return typeof location !== "undefined" ? location.href : "";
    } catch {
      return "";
    }
  }

  private createIdempotencyKey(sessionId: string, sequence: number, byteSize: number): string {
    return `${this.webId}:${sessionId}:${sequence}:${byteSize}`;
  }

  private measureEvents(events: unknown[]): number {
    try {
      return JSON.stringify(events).length;
    } catch {
      return 0;
    }
  }

  private toError(error: unknown): Error {
    return error instanceof Error ? error : new Error(String(error));
  }

  /**
   * The current replay session id, rotating after SESSION_IDLE_TIMEOUT_MS of
   * inactivity.
   *
   * Reusing the stored id is only correct while the visit is still going. The
   * id survives navigation on purpose (chunk sequences continue across page
   * loads — see SEQ_STORAGE_PREFIX), but sessionStorage's own lifetime is the
   * browser tab, which is far longer than a visit. Pairing the id with a touch
   * timestamp is what bounds a session to an actual visit.
   */
  private resolveSessionId(): string {
    // One session per visit across the SDK. Replay used to mint its own id, so
    // a recording, the session id in checkout metadata and the visit's events
    // were three different sessions — a payment could never find its replay.
    if (this.identityManager) return this.identityManager.getSessionId();
    try {
      const existing = sessionStorage.getItem(STORAGE_KEY);
      const lastTouch = Number(sessionStorage.getItem(TOUCH_STORAGE_KEY));
      const isFresh =
        Number.isFinite(lastTouch) && Date.now() - lastTouch < SESSION_IDLE_TIMEOUT_MS;

      if (existing && isFresh) {
        this.touchSession();
        return existing;
      }

      // Expired (or an id stored before touch tracking existed): start a new
      // session. The old id's persisted sequence counter is left alone — it is
      // namespaced per session id and expires with sessionStorage.
      const fresh = this.generateId();
      sessionStorage.setItem(STORAGE_KEY, fresh);
      this.touchSession();
      return fresh;
    } catch {
      return this.generateId();
    }
  }

  /**
   * Marks the replay session as active. Called when a session id is resolved
   * and on every flush — a recording that is still shipping chunks is by
   * definition not idle, and without this a recording longer than the idle
   * timeout would rotate its own id mid-session and split one visit in two.
   */
  private touchSession(): void {
    if (this.identityManager) {
      // Reading the id IS the touch (IdentityManager refreshes its timestamp).
      this.identityManager.getSessionId();
      return;
    }
    try {
      sessionStorage.setItem(TOUCH_STORAGE_KEY, String(Date.now()));
    } catch {
      // sessionStorage unavailable (private mode, sandboxed iframe) — the
      // session id falls back to per-run ids, which is safe: it over-splits
      // sessions rather than merging unrelated ones.
    }
  }

  private generateId(): string {
    const cryptoObj = (typeof crypto !== "undefined" ? crypto : undefined) as Crypto | undefined;
    if (cryptoObj?.randomUUID) return cryptoObj.randomUUID();
    return `rep_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
  }
}

export default SessionReplay;
